import React from "react";
import MaterialTable from "material-table";
import { Column } from "material-table";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormControl from '@mui/material/FormControl';
import FormLabel from '@mui/material/FormLabel';
import tableIcons from "../Displays/MaterialTableIcons";
import {
	useGetGlobalInventoryReport
} from "../../queries/GlobalInventoryReport";
import { useGetOperationalTeams }  from '../../queries/AttorneyInfo';
import { useGetFormulasQuery, useGetStats } from '../../queries/Formulas';
import PageLoader from '../Displays/Loading';

const math = require('mathjs');

const formatter = new Intl.NumberFormat("en-US", {
	style: "currency",
	currency: "USD",
});

function formatDateTimeString(dateString: string, includeTime = false) {
	// 1. Parse the input string into a Date object. The format provided is standard
	// and recognized by the Date constructor.
	const date = new Date(dateString);

	// 2. Validate the date object. getTime() returns NaN for invalid dates.
	if (isNaN(date.getTime())) {
		return "Invalid Date Input: Please ensure the format is correct.";
	}

	// 3. Extract Date components using UTC methods to ensure the output matches
	// the explicit GMT time in the input, regardless of the user's local timezone.

	// Get UTC Year
	const year = date.getUTCFullYear();

	// Get UTC Month (0-indexed, so add 1) and pad with a leading zero if needed
	const month = String(date.getUTCMonth() + 1).padStart(2, '0');

	// Get UTC Day and pad
	const day = String(date.getUTCDate()).padStart(2, '0');

	// Start building the output string
	let output = `${year}-${month}-${day}`;

	// 4. Optionally include the time components
	if (includeTime) {
		// Get UTC Hours, Minutes, and Seconds and pad them
		const hours = String(date.getUTCHours()).padStart(2, '0');
		const minutes = String(date.getUTCMinutes()).padStart(2, '0');
		const seconds = String(date.getUTCSeconds()).padStart(2, '0');

		// Append time to the date
		output += ` ${hours}:${minutes}:${seconds}`;
	}

	return output;
}
const isInvalidDate = (dateValue: any): boolean => {
	if (!dateValue || dateValue === "None" || dateValue === "") return true;
	const d = new Date(dateValue);
	if (isNaN(d.getTime())) return true;
	if (d.getFullYear() < 2000) return true;
	return false;
};

const defaultMaterialTheme = createTheme();

export const buildExpressionString = (expr: any) => {
	const toString = (node: any) => {
	  if (!node || typeof node !== "object") return "";
  
	  switch (node.type) {
		case "constant":
		  return node.value.toString();
  
		case "variable":
		  return node.name;
  
		case "operation":
		  const left: any = toString(node.left);
		  const right: any = toString(node.right);
		  return `(${left} ${node.operator} ${right})`;
  
		default:
		  throw new Error("Unknown node type: " + node.type);
	  }
	}
  
	return `${toString(expr)}`;
}

export const find_all_dependencies = (expr: any) => {
	const search = (node: any) => {
	  if (!node || typeof node !== "object") return "";
  
	  switch (node.type) {
		case "constant":
		  return [];
  
		case "variable":
		  return [node.name];
  
		case "operation":
		  const left: any = search(node.left);
		  const right: any = search(node.right);
		  return [...left, ...right];
  
		default:
		  return [];
	  }
	}
  
	return search(expr);
}

export const formula_order = (formulaData: any) => {
	const dependencies: any = {};
	const expressions: any = {};
	const variables: any = formulaData.map( (formula: any) => {
		dependencies[formula.variable_name] = find_all_dependencies(formula.formula_object);
		expressions[formula.variable_name] = buildExpressionString(formula.formula_object);

		return formula.variable_name
	});

	const newOrder = resolveDependencies(dependencies, variables);

	return { newOrder, dependencies, expressions };
}

export const resolveDependencies = (dependencyDict: any, variables: any) => {
    const result: any = [];
    const visited: any = new Set();
    const temp: any = new Set(); // for cycle detection

    function visit(node: any) {
        if (visited.has(node)) return;
        if (temp.has(node)) {
            throw new Error(`Cycle detected involving ${node}`);
        }

        temp.add(node);

        const dependencies = dependencyDict[node] || [];
        for (const dep of dependencies) {
            visit(dep);
        }

        temp.delete(node);
        visited.add(node);
		if(variables.includes(node))
        	result.push(node);
    }

    for (const node in dependencyDict) {
        visit(node);
    }

    return result;
}

const generateData = (inventoryData: any, teamData: any, formulas: any, selectedTeam: string, totalOpenCases: number = 269) => {
	const results: any[] = [];
	const teamDict: any = {};

	teamData.forEach( (team: any) => {
		teamDict[team.team_name] = team.operational_costs;
	});

	const enabledFormulas = formulas.filter( (formula:any) => formula.enabled );
	const { newOrder, dependencies, expressions } = formula_order(enabledFormulas);
	const totalsRow: any = {
		"anticipated_attorney_fee": 0,
		"attorney_team": selectedTeam,
		"case": "Total",
		"case_class": "",
		"case_type": "",
		"conference_requested": "",
		"current_step": "",
		"date_case_retained": "",
		"date_of_loss": "",
		"days_in_inventory": "",
		"liability_description": "",
		"litigation_date": "",
		"matter": "",
		"mediation_scheduled": "",
		"open_closed": true,
		"pfs_served": "",
		"projected_date_of_anticipated_attorney_fee": "",
		"referall_fee": "",
		"trial_order": "",
		"attorney_fee": 0,
		"open_cases": "",
		"days_in_inventory_lit": "",
		"days_in_inventory_prelit": "",
		"annual_expenses": "",
		"daily_expenses": "",
		"case_operation_fee": 0,
		"net_revenue": 0,
		'daily_legal_team_expenses': "",
		'annual_legal_expenses': "",
		'case_legal_team_fee': 0,
		'total_case_admin_expenses_current': 0,
		'total_case_legal_expenses_current': 0,
	};

	const dollars = [
		'overhead_cost',
		'anticipated_litigation',
		'overhead_operational_costs',
		'case_operation_fee',
		'net_revenue',
		'annual_expenses',
		'daily_expenses',
		'daily_legal_team_expenses',
		'annual_legal_expenses',
		'case_legal_team_fee',
		'anticipated_attorney_fee'
	];

	inventoryData.forEach( (x: any) => {
		const t = JSON.parse(JSON.stringify(x));
		let table_object: any = {};
		t["attorney_fee"] = teamDict[`${t["attorney_team"]}`] || 0.4;
		t["open_cases"] = totalOpenCases;

		let pastDate = new Date(x["date_case_retained"]);

		// Create a Date object for the current day.
		let today = new Date();

		// Set the time for both dates to midnight UTC to get a clean day count.
		// This prevents discrepancies from time zone offsets.
		let pastDateUTC = Date.UTC(pastDate.getFullYear(), pastDate.getMonth(), pastDate.getDate());
		let todayUTC = Date.UTC(today.getFullYear(), today.getMonth(), today.getDate());

		// Calculate the difference in milliseconds.
		let msDifference = todayUTC - pastDateUTC;

		// Convert the difference from milliseconds to days.
		let oneDayInMs = 1000 * 60 * 60 * 24;
		let daysDifference = Math.round(msDifference / oneDayInMs);

		if (pastDate.getFullYear() < 2000) {
			daysDifference = 0;
		}

		t["days_in_inventory"] = daysDifference || 0;

		for( const index in newOrder ) {
			const v = newOrder[index];
			t[v] = math.evaluate(expressions[v], t).toFixed(2);
		}

		const hasInvalidDates = isInvalidDate(x["date_case_retained"]) || isInvalidDate(x["projected_date_of_anticipated_attorney_fee"]);

		// Calculate Case Daily Expenses
		const caseDailyAdmin = Number(t["daily_expenses"]) / Number(t["open_cases"]);
		const caseDailyLegal = Number(t["daily_legal_team_expenses"]) / Number(t["open_cases"]);

		// Calculate Total Case Admin Expenses (Current)
		let totalCaseAdminCurrent = Number(t["days_in_inventory"]) * caseDailyAdmin;

		// Calculate Total Case Legal Team Expenses (Current)
		let totalCaseLegalCurrent = Number(t["days_in_inventory"]) * caseDailyLegal;

		// Override case_operation_fee to be the sum of Admin + Legal (matching Excel)
		t["case_operation_fee"] = (totalCaseAdminCurrent + totalCaseLegalCurrent).toFixed(2);

		// Calculate Net Profit Margin (Current) = Net Profit (Current) / Anticipated Attorney Fee
		if (Number(t["anticipated_attorney_fee"]) !== 0) {
			t["net_profit_margin_current"] = (Number(t["net_revenue"]) / Number(t["anticipated_attorney_fee"])).toFixed(4);
		} else {
			t["net_profit_margin_current"] = "0";
		}

		if (hasInvalidDates) {
			t["case_operation_fee"] = "0.00";
			t["case_legal_team_fee"] = "0.00";
			t["net_revenue"] = "0.00";
			t["net_profit_margin_current"] = "0";
			totalCaseAdminCurrent = 0;
			totalCaseLegalCurrent = 0;
		}

		if(selectedTeam === t['attorney_team']){
			totalsRow['net_revenue'] += Number(t['net_revenue']);
			totalsRow['anticipated_attorney_fee'] += Number(t['anticipated_attorney_fee']);
			totalsRow['attorney_fee'] += Number(t['attorney_fee']);
			totalsRow['case_operation_fee'] += Number(t['case_operation_fee']);
			totalsRow['case_legal_team_fee'] += Number(t['case_legal_team_fee']);
			totalsRow['daily_legal_team_expenses'] += Number(t['daily_legal_team_expenses']);
			totalsRow['annual_legal_expenses'] += Number(t['annual_legal_expenses']);
			totalsRow['total_case_admin_expenses_current'] = (Number(totalsRow['total_case_admin_expenses_current'] || 0) + totalCaseAdminCurrent);
			totalsRow['total_case_legal_expenses_current'] = (Number(totalsRow['total_case_legal_expenses_current'] || 0) + totalCaseLegalCurrent);
		}

		if(selectedTeam === 'All'){
			totalsRow['net_revenue'] += Number(t['net_revenue']);
			totalsRow['anticipated_attorney_fee'] += Number(t['anticipated_attorney_fee']);
			totalsRow['attorney_fee'] += Number(t['attorney_fee']);
			totalsRow['case_operation_fee'] += Number(t['case_operation_fee']);
			totalsRow['case_legal_team_fee'] += Number(t['case_legal_team_fee']);
			//totalsRow['daily_legal_team_expenses'] += Number(t['daily_legal_team_expenses']);
			//totalsRow['annual_legal_expenses'] += Number(t['annual_legal_expenses']);
			totalsRow['total_case_admin_expenses_current'] = (Number(totalsRow['total_case_admin_expenses_current'] || 0) + totalCaseAdminCurrent);
			totalsRow['total_case_legal_expenses_current'] = (Number(totalsRow['total_case_legal_expenses_current'] || 0) + totalCaseLegalCurrent);
		}
		t["projected_date_of_anticipated_attorney_fee"] = formatDateTimeString(t["projected_date_of_anticipated_attorney_fee"], false) === "1900-01-01" ? "" : formatDateTimeString(t["projected_date_of_anticipated_attorney_fee"], false);
		t["date_of_loss"] = formatDateTimeString(t["date_of_loss"], false) === "1900-01-01" ? "" : formatDateTimeString(t["date_of_loss"], false);
		t["date_case_retained"] = t["date_case_retained"] === "None" ? "" : t["date_case_retained"];

		Object.keys(t).forEach( (key: any) => {
			if(dollars.includes(`${key}`)){
				table_object[key] = Math.round(Number(t[key]) * 100) / 100;
			}else{
				table_object[key] = t[key];
			}
		})
		//table_object['net_revenue'] = `${t["days_in_inventory_prelit"] > 0 ? formatter.format(t["net_revenue"]) : formatter.format((Number(t["net_revenue"]) + Number(t["overhead_operational_costs"]) )) }`;

		results.push(table_object)
	})

	Object.keys(totalsRow).forEach( (key: any) => {
		if(dollars.includes(`${key}`)){
			totalsRow[key] = Math.round(Number(totalsRow[key]) * 100) / 100;
		}else{
			totalsRow[key] = totalsRow[key];
		}
	})

	totalsRow['days_in_inventory_lit'] = "";
	totalsRow['days_in_inventory_prelit'] = "";
	results.push(totalsRow);

	return results;
}

const generateSimluatedData = (inventoryData: any, teamData: any, formulas: any, selectedTeam: string, totalOpenCases: number = 269) => {
	const results: any[] = [];
	const teamDict: any = {};

	teamData.forEach( (team: any) => {
		teamDict[team.team_name] = team.operational_costs;
	});

	const enabledFormulas = formulas.filter( (formula:any) => formula.enabled );
	const { newOrder, dependencies, expressions } = formula_order(enabledFormulas);
	const totalsRow: any = {
		"anticipated_attorney_fee": 0,
		"attorney_team": selectedTeam,
		"case": "Total",
		"case_class": "",
		"case_type": "",
		"conference_requested": "",
		"current_step": "",
		"date_case_retained": "",
		"date_of_loss": "",
		"days_in_inventory": "",
		"liability_description": "",
		"litigation_date": "",
		"matter": "",
		"mediation_scheduled": "",
		"open_closed": true,
		"pfs_served": "",
		"projected_date_of_anticipated_attorney_fee": "",
		"referall_fee": "",
		"trial_order": "",
		"attorney_fee": 0,
		"open_cases": "",
		"days_in_inventory_lit": "",
		"days_in_inventory_prelit": "",
		"annual_expenses": "",
		"daily_expenses": "",
		"case_operation_fee": 0,
		"net_revenue": 0,
		'daily_legal_team_expenses': "",
		'annual_legal_expenses': "",
		'case_legal_team_fee': 0,
		'net_revenue_simulated': 0,
		"total_case_admin_expenses_simulated": 0,
		"total_case_legal_expenses_simulated": 0,
		"gross_profit_simulated": 0,
		"gross_profit_margin_simulated": 0,
		"net_profit_margin_simulated": 0,
		'case_operation_fee_simulated': 0,
		'total_case_admin_expenses_current': 0,
		'total_case_legal_expenses_current': 0,
		'gross_profit_current': 0,
	};

	const dollars = [
		'overhead_cost',
		'anticipated_litigation',
		'overhead_operational_costs',
		'case_operation_fee',
		'net_revenue',
		'annual_expenses',
		'daily_expenses',
		'daily_legal_team_expenses',
		'annual_legal_expenses',
		'case_legal_team_fee',
		'anticipated_attorney_fee'
	];

	inventoryData.forEach( (x: any) => {
		const t = JSON.parse(JSON.stringify(x));
		let table_object: any = {};
		t["attorney_fee"] = teamDict[`${t["attorney_team"]}`] || 0.4;
		t["open_cases"] = totalOpenCases;

		let pastDate = new Date(x["date_case_retained"]);
		let projectedDate = new Date(x["projected_date_of_anticipated_attorney_fee"]);
		let today = new Date();
		
		// Set the time for both dates to midnight UTC to get a clean day count.
		// This prevents discrepancies from time zone offsets.
		let pastDateUTC = Date.UTC(pastDate.getFullYear(), pastDate.getMonth(), pastDate.getDate());
		let todayUTC = Date.UTC(today.getFullYear(), today.getMonth(), today.getDate());
		let projectedDateUTC = Date.UTC(projectedDate.getFullYear(), projectedDate.getMonth(), projectedDate.getDate());

		// Calculate CURRENT days in inventory (from date retained to today)
		let msDifferenceCurrent = todayUTC - pastDateUTC;
		let oneDayInMs = 1000 * 60 * 60 * 24;
		let daysDifferenceCurrent = Math.round(msDifferenceCurrent / oneDayInMs);
		if (pastDate.getFullYear() < 2000) {
			daysDifferenceCurrent = 0;
		}

		// Calculate LIFE days in inventory (from date retained to projected date)
		let msDifferenceLife = Math.abs(projectedDateUTC - pastDateUTC);
		let daysDifferenceLife = Math.round(msDifferenceLife / oneDayInMs);
		if ((pastDate.getFullYear() < 2000) || (projectedDate.getFullYear() < 2000)) {
			daysDifferenceLife = 0;
		}

		// Store both values
		t["days_in_inventory_current"] = daysDifferenceCurrent || 0;
		t["days_in_inventory"] = daysDifferenceLife || 0; // This is the life of case value

		for( const index in newOrder ) {
			const v = newOrder[index];
			t[v] = math.evaluate(expressions[v], t).toFixed(2);
		}

		const hasInvalidDates = isInvalidDate(x["date_case_retained"]) || isInvalidDate(x["projected_date_of_anticipated_attorney_fee"]);

		// Calculate additional "Life of Case" (Projected) values that match Excel formulas
		// Case Daily Admin = daily_expenses / open_cases
		// Case Daily Legal = daily_legal_team_expenses / open_cases
		const caseDailyAdmin = Number(t["daily_expenses"]) / Number(t["open_cases"]);
		const caseDailyLegal = Number(t["daily_legal_team_expenses"]) / Number(t["open_cases"]);
		
		// Store case daily expenses
		t["case_daily_admin_expenses"] = caseDailyAdmin.toFixed(2);
		t["case_daily_legal_expenses"] = caseDailyLegal.toFixed(2);
		
		// Total Case Admin Expenses (Current) = Days In Inventory (current) * Case Daily Admin
		t["total_case_admin_expenses_current"] = (Number(t["days_in_inventory_current"]) * caseDailyAdmin).toFixed(2);
		
		// Total Case Admin Expenses (Life) = Days In Inventory (Life) * Case Daily Admin
		t["total_case_admin_expenses_simulated"] = (Number(t["days_in_inventory"]) * caseDailyAdmin).toFixed(2);
		
		// Total Case Legal Team Expenses (Current) = Days In Inventory (current) * Case Daily Legal
		t["total_case_legal_expenses_current"] = (Number(t["days_in_inventory_current"]) * caseDailyLegal).toFixed(2);
		
		// Total Case Legal Team Expenses (Life) = Days In Inventory (Life) * Case Daily Legal
		t["total_case_legal_expenses_simulated"] = (Number(t["days_in_inventory"]) * caseDailyLegal).toFixed(2);
		
		// Total Case Operation Cost (Current) = Total Case Admin Expenses (Current) + Total Case Legal Team Expenses (Current)
		t["case_operation_fee"] = (Number(t["total_case_admin_expenses_current"]) + Number(t["total_case_legal_expenses_current"])).toFixed(2);
		
		// Total Case Operation Cost (Life) = Total Case Admin Expenses (Life) + Total Case Legal Team Expenses (Life)
		t["case_operation_fee_simulated"] = (Number(t["total_case_admin_expenses_simulated"]) + Number(t["total_case_legal_expenses_simulated"])).toFixed(2);
		
		// Gross Profit (Current) = Anticipated Attorney Fee - Total Case Legal (Current)
		t["gross_profit_current"] = (Number(t["anticipated_attorney_fee"]) - Number(t["total_case_legal_expenses_current"])).toFixed(2);
		
		// Gross Profit Margin (Current) = Gross Profit (Current) / Anticipated Attorney Fee
		if (Number(t["anticipated_attorney_fee"]) !== 0) {
			t["gross_profit_margin_current"] = (Number(t["gross_profit_current"]) / Number(t["anticipated_attorney_fee"])).toFixed(4);
		} else {
			t["gross_profit_margin_current"] = "0";
		}
		
		// Net Profit Margin (Current) = Net Profit (Current) / Anticipated Attorney Fee
		if (Number(t["anticipated_attorney_fee"]) !== 0) {
			t["net_profit_margin_current"] = (Number(t["net_revenue"]) / Number(t["anticipated_attorney_fee"])).toFixed(4);
		} else {
			t["net_profit_margin_current"] = "0";
		}
		
		// Gross Profit (Life) = Anticipated Attorney Fee - Total Case Legal (Life)
		t["gross_profit_simulated"] = (Number(t["anticipated_attorney_fee"]) - Number(t["total_case_legal_expenses_simulated"])).toFixed(2);
		
		// Gross Profit Margin (Life) = Gross Profit (Life) / Anticipated Attorney Fee
		// Net Profit Margin (Life) = Net Profit (Life) / Anticipated Attorney Fee
		if (Number(t["anticipated_attorney_fee"]) !== 0) {
			t["gross_profit_margin_simulated"] = (Number(t["gross_profit_simulated"]) / Number(t["anticipated_attorney_fee"])).toFixed(4);
			t["net_profit_margin_simulated"] = (Number(t["net_revenue"]) / Number(t["anticipated_attorney_fee"])).toFixed(4);
		} else {
			t["gross_profit_margin_simulated"] = "0";
			t["net_profit_margin_simulated"] = "0";
		}

		if (hasInvalidDates) {
			t["case_operation_fee"] = "0.00";
			t["case_operation_fee_simulated"] = "0.00";
			t["case_legal_team_fee"] = "0.00";
			t["net_revenue"] = "0.00";
			t["total_case_admin_expenses_current"] = "0.00";
			t["total_case_admin_expenses_simulated"] = "0.00";
			t["total_case_legal_expenses_current"] = "0.00";
			t["total_case_legal_expenses_simulated"] = "0.00";
			t["gross_profit_current"] = "0.00";
			t["gross_profit_simulated"] = "0.00";
			t["gross_profit_margin_current"] = "0";
			t["gross_profit_margin_simulated"] = "0";
			t["net_profit_margin_current"] = "0";
			t["net_profit_margin_simulated"] = "0";
		}

		if(selectedTeam === t['attorney_team']){
			totalsRow['net_revenue'] += Number(t['net_revenue']);
			totalsRow['anticipated_attorney_fee'] += Number(t['anticipated_attorney_fee']);
			totalsRow['attorney_fee'] += Number(t['attorney_fee']);
			totalsRow['case_operation_fee'] += Number(t['case_operation_fee']);
			totalsRow['case_operation_fee_simulated'] += Number(t['case_operation_fee_simulated']);
			totalsRow['case_legal_team_fee'] += Number(t['case_legal_team_fee']);
			//totalsRow['daily_legal_team_expenses'] += Number(t['daily_legal_team_expenses']);
			//totalsRow['annual_legal_expenses'] += Number(t['annual_legal_expenses']);
			totalsRow['total_case_admin_expenses_current'] = (Number(totalsRow['total_case_admin_expenses_current'] || 0) + Number(t['total_case_admin_expenses_current']));
			totalsRow['total_case_admin_expenses_simulated'] = (Number(totalsRow['total_case_admin_expenses_simulated'] || 0) + Number(t['total_case_admin_expenses_simulated']));
			totalsRow['total_case_legal_expenses_current'] = (Number(totalsRow['total_case_legal_expenses_current'] || 0) + Number(t['total_case_legal_expenses_current']));
			totalsRow['total_case_legal_expenses_simulated'] = (Number(totalsRow['total_case_legal_expenses_simulated'] || 0) + Number(t['total_case_legal_expenses_simulated']));
			totalsRow['gross_profit_current'] = (Number(totalsRow['gross_profit_current'] || 0) + Number(t['gross_profit_current']));
			totalsRow['gross_profit_simulated'] = (Number(totalsRow['gross_profit_simulated'] || 0) + Number(t['gross_profit_simulated']));
		}

		if(selectedTeam === 'All'){
			totalsRow['net_revenue'] += Number(t['net_revenue']);
			totalsRow['anticipated_attorney_fee'] += Number(t['anticipated_attorney_fee']);
			totalsRow['attorney_fee'] += Number(t['attorney_fee']);
			totalsRow['case_operation_fee'] += Number(t['case_operation_fee']);
			totalsRow['case_operation_fee_simulated'] += Number(t['case_operation_fee_simulated']);
			totalsRow['case_legal_team_fee'] += Number(t['case_legal_team_fee']);
			//totalsRow['daily_legal_team_expenses'] += Number(t['daily_legal_team_expenses']);
			//totalsRow['annual_legal_expenses'] += Number(t['annual_legal_expenses']);
			totalsRow['total_case_admin_expenses_current'] = (Number(totalsRow['total_case_admin_expenses_current'] || 0) + Number(t['total_case_admin_expenses_current']));
			totalsRow['total_case_admin_expenses_simulated'] = (Number(totalsRow['total_case_admin_expenses_simulated'] || 0) + Number(t['total_case_admin_expenses_simulated']));
			totalsRow['total_case_legal_expenses_current'] = (Number(totalsRow['total_case_legal_expenses_current'] || 0) + Number(t['total_case_legal_expenses_current']));
			totalsRow['total_case_legal_expenses_simulated'] = (Number(totalsRow['total_case_legal_expenses_simulated'] || 0) + Number(t['total_case_legal_expenses_simulated']));
			totalsRow['gross_profit_current'] = (Number(totalsRow['gross_profit_current'] || 0) + Number(t['gross_profit_current']));
			totalsRow['gross_profit_simulated'] = (Number(totalsRow['gross_profit_simulated'] || 0) + Number(t['gross_profit_simulated']));
		}
		t["projected_date_of_anticipated_attorney_fee"] = formatDateTimeString(t["projected_date_of_anticipated_attorney_fee"], false) === "1900-01-01" ? "" : formatDateTimeString(t["projected_date_of_anticipated_attorney_fee"], false);
		t["date_of_loss"] = formatDateTimeString(t["date_of_loss"], false) === "1900-01-01" ? "" : formatDateTimeString(t["date_of_loss"], false);
		t["date_case_retained"] = t["date_case_retained"] === "None" ? "" : t["date_case_retained"];

		Object.keys(t).forEach( (key: any) => {
			if(dollars.includes(`${key}`)){
				table_object[key] = Math.round(Number(t[key]) * 100) / 100;
			}else{
				table_object[key] = t[key];
			}
		})
		//table_object['net_revenue'] = `${t["days_in_inventory_prelit"] > 0 ? formatter.format(t["net_revenue"]) : formatter.format((Number(t["net_revenue"]) + Number(t["overhead_operational_costs"]))) }`;
		results.push(table_object)
	})

	Object.keys(totalsRow).forEach( (key: any) => {
		if(dollars.includes(`${key}`)){
			totalsRow[key] = Math.round(Number(totalsRow[key]) * 100) / 100;
		}else{
			totalsRow[key] = totalsRow[key];
		}
	})

	totalsRow['days_in_inventory_lit'] = "";
	totalsRow['days_in_inventory_prelit'] = "";
	results.push(totalsRow);

	return results;
}

const VariableQuotaLedgerTable = () => {
	const {data, isFetching, isError} = useGetGlobalInventoryReport();
	const {data: teamData, isFetching: teamFetching, isError: teamError } = useGetOperationalTeams();
	const {data: formulaData, isFetching: formulasFetching, isError: formulaError} = useGetFormulasQuery();
	const {data: statsData, isFetching: statsFetching, isError: statsError} = useGetStats();
	const [selectedTeam, setSelectedValue] = React.useState('All');

	const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		setSelectedValue(event.target.value);
	};
	
	if (isFetching || teamFetching || formulasFetching || statsFetching) 
		return <PageLoader />;

	if (isError || teamError || formulaError || statsError) 
		return <div>Error loading members.</div>;
	

	if (!formulaData || !teamData || !data || !statsData) 
		return <div>Error loading members.</div>;

	// Get total open cases from the actual inventory data (like Overview page does)
	const totalOpenCases = data?.length || 1;
	
	const enabledFormulas = formulaData.filter( (formula:any) => formula.enabled );
	
	const columns: Column<any>[] = [
		// Column ordering matching Excel spreadsheet
		{title: "Case", field: "case"},
		{title: "Matter", field: "matter"},
		{title: "Case Type", field: "case_type"},
		{title: "Attorney Team", field: "attorney_team"},
		{title: "Total Company Open Cases", field: "open_cases"},
		{
			title: "Company Annual Admin Expenses",
			field: "annual_expenses",
			render: (rowData: any) => formatter.format(rowData["annual_expenses"] || 0),
		},
		{
			title: "Company Annual Legal Team Expenses",
			field: "annual_legal_expenses",
			render: (rowData: any) => formatter.format(rowData["annual_legal_expenses"] || 0),
		},
		{
			title: "Company Daily Admin Expenses",
			field: "daily_expenses",
			render: (rowData: any) => formatter.format(rowData["daily_expenses"] || 0),
		},
		{
			title: "Company Daily Legal Team Expenses",
			field: "daily_legal_team_expenses",
			render: (rowData: any) => formatter.format(rowData["daily_legal_team_expenses"] || 0),
		},
		{
			title: "Case Daily Admin Expenses",
			field: "case_daily_admin_expenses",
			render: (rowData: any) =>
				rowData["case_daily_admin_expenses"] === undefined
					? ""
					: formatter.format(Number(rowData["case_daily_admin_expenses"])),
		},
		{
			title: "Case Daily Legal Team Expenses",
			field: "case_daily_legal_expenses",
			render: (rowData: any) =>
				rowData["case_daily_legal_expenses"] === undefined
					? ""
					: formatter.format(Number(rowData["case_daily_legal_expenses"])),
		},
		{
			title: "Date Case Retained",
			width: "5%",
			field: "date_case_retained",
			render: (rowData: any) => rowData["date_case_retained"] === "None" ? "" : rowData["date_case_retained"]
		},
		{
			title: "Life of the case Date of Anticipated Attorney Fee",
			field: "projected_date_of_anticipated_attorney_fee",
			width: "200px"
		},
		{
			title: "Anticipated Attorney Fee",
			field: "anticipated_attorney_fee",
			render: (rowData: any) => formatter.format(rowData["anticipated_attorney_fee"]),
		},
		{title: "Days In Inventory (as of today's date)", field: "days_in_inventory"},
		{
			title: "Days In Inventory (Life of case)",
			field: "days_in_inventory_simulated",
		},
		{
			title: "Total Case Admin Expenses (Current)",
			field: "total_case_admin_expenses_current",
			render: (rowData: any) =>
				rowData["total_case_admin_expenses_current"] === undefined
					? ""
					: formatter.format(Number(rowData["total_case_admin_expenses_current"])),
		},
		{
			title: "Total Case Admin Expenses (Life of Case)",
			field: "total_case_admin_expenses_simulated",
			render: (rowData: any) =>
				rowData["total_case_admin_expenses_simulated"] === undefined
					? ""
					: formatter.format(Number(rowData["total_case_admin_expenses_simulated"])),
		},
		{
			title: "Total Case Legal Team Expenses (Current)",
			field: "total_case_legal_expenses_current",
			render: (rowData: any) =>
				rowData["total_case_legal_expenses_current"] === undefined
					? ""
					: formatter.format(Number(rowData["total_case_legal_expenses_current"])),
		},
		{
			title: "Total Case Legal Team Expenses (Life of Case)",
			field: "total_case_legal_expenses_simulated",
			render: (rowData: any) =>
				rowData["total_case_legal_expenses_simulated"] === undefined
					? ""
					: formatter.format(Number(rowData["total_case_legal_expenses_simulated"])),
		},
		{
			title: "Total Case Operation Cost (Current)",
			field: "case_operation_fee",
			render: (rowData: any) => formatter.format(rowData["case_operation_fee"] || 0),
		},
		{
			title: "Total Case Operation Cost (Life of Case)",
			field: "case_operation_fee_simulated",
			render: (rowData: any) =>
				rowData["case_operation_fee_simulated"] === undefined
					? ""
					: formatter.format(Number(rowData["case_operation_fee_simulated"])),
		},
		{
			title: "Gross Profit (Current)",
			field: "gross_profit_current",
			render: (rowData: any) =>
				rowData["gross_profit_current"] === undefined
					? ""
					: formatter.format(Number(rowData["gross_profit_current"])),
		},
		{
			title: "Gross Profit Margin (Current)",
			field: "gross_profit_margin_current",
			render: (rowData: any) =>
				rowData["gross_profit_margin_current"] === undefined
					? ""
					: `${(Number(rowData["gross_profit_margin_current"]) * 100).toFixed(2)}%`,
		},
		{
			title: "Net Profit (Current)",
			field: "net_revenue",
			render: (rowData: any) => formatter.format(rowData["net_revenue"] || 0),
		},
		{
			title: "Net Profit Margin (Current)",
			field: "net_profit_margin_current",
			render: (rowData: any) =>
				rowData["net_profit_margin_current"] === undefined
					? ""
					: `${(Number(rowData["net_profit_margin_current"]) * 100).toFixed(2)}%`,
		},
		{
			title: "Gross Profit (Life of Case)",
			field: "gross_profit_simulated",
			render: (rowData: any) =>
				rowData["gross_profit_simulated"] === undefined
					? ""
					: formatter.format(Number(rowData["gross_profit_simulated"])),
		},
		{
			title: "Gross Profit Margin (Life of Case)",
			field: "gross_profit_margin_simulated",
			render: (rowData: any) =>
				rowData["gross_profit_margin_simulated"] === undefined
					? ""
					: `${(Number(rowData["gross_profit_margin_simulated"]) * 100).toFixed(2)}%`,
		},
		{
			title: "Net Profit (Life of Case)",
			field: "net_revenue_simulated",
			render: (rowData: any) =>
				rowData["net_revenue_simulated"] === undefined
					? ""
					: formatter.format(Number(rowData["net_revenue_simulated"])),
		},
		{
			title: "Net Profit Margin (Life of Case)",
			field: "net_profit_margin_simulated",
			render: (rowData: any) =>
				rowData["net_profit_margin_simulated"] === undefined
					? ""
					: `${(Number(rowData["net_profit_margin_simulated"]) * 100).toFixed(2)}%`,
		},
	];
	
	const dollars = [
		'anticipated_attorney_fee',
		'overhead_cost',
		'anticipated_litigation',
		'overhead_operational_costs',
		'case_operation_fee',
		'net_revenue',
		'annual_expenses',
		'daily_expenses',
		'daily_legal_team_expenses',
		'annual_legal_expenses',
		'case_legal_team_fee'
	];

	// Note: Removed enabledFormulas loop to match Excel spreadsheet exactly (30 columns only)

	const rawCurrentData = generateData(data, teamData, formulaData, selectedTeam, totalOpenCases);
	const rawSimulatedData = generateSimluatedData(data, teamData, formulaData, selectedTeam, totalOpenCases);

	const currentData = rawCurrentData.filter(
		(col: any) => (selectedTeam === 'All' ? true : (selectedTeam === col["attorney_team"]))
	);
	const simulatedData = rawSimulatedData.filter(
		(col: any) => (selectedTeam === 'All' ? true : (selectedTeam === col["attorney_team"]))
	);

	const simulatedByMatter: any = {};
	simulatedData.forEach((row: any) => {
		if (row.case !== "Total" && row.matter) {
			simulatedByMatter[row.matter] = row;
		}
	});

	const simulatedTotals = simulatedData.find((row: any) => row.case === "Total");

	const tableData = currentData.map((row: any) => {
		const source =
			row.case === "Total"
				? simulatedTotals
				: simulatedByMatter[row.matter] || null;

		if (!source) {
			return row;
		}

		return {
			...row,
			net_revenue_simulated: source.net_revenue,
			anticipated_attorney_fee_simulated: source.anticipated_attorney_fee,
			attorney_fee_simulated: source.attorney_fee,
			case_operation_fee_simulated: source.case_operation_fee_simulated,
			case_legal_team_fee_simulated: source.case_legal_team_fee,
			daily_legal_team_expenses_simulated: source.daily_legal_team_expenses,
			annual_legal_expenses_simulated: source.annual_legal_expenses,
			days_in_inventory_simulated: source.days_in_inventory,
			days_in_inventory_current: source.days_in_inventory_current,
			daily_expenses_simulated: source.daily_expenses,
			annual_expenses_simulated: source.annual_expenses,
			case_daily_admin_expenses: source.case_daily_admin_expenses,
			case_daily_legal_expenses: source.case_daily_legal_expenses,
			total_case_admin_expenses_current: source.total_case_admin_expenses_current,
			total_case_admin_expenses_simulated: source.total_case_admin_expenses_simulated,
			total_case_legal_expenses_current: source.total_case_legal_expenses_current,
			total_case_legal_expenses_simulated: source.total_case_legal_expenses_simulated,
			gross_profit_current: source.gross_profit_current,
			gross_profit_margin_current: source.gross_profit_margin_current,
			// Note: net_profit_margin_current comes from row (current data), not source (simulated data)
			gross_profit_simulated: source.gross_profit_simulated,
			gross_profit_margin_simulated: source.gross_profit_margin_simulated,
			net_profit_margin_simulated: source.net_profit_margin_simulated,
		};
	});

	return (
		<ThemeProvider theme={defaultMaterialTheme}>
			<FormControl>
				<FormLabel id="demo-row-radio-buttons-group-label">Attorney Team</FormLabel>
				<RadioGroup
					row
					aria-labelledby="demo-row-radio-buttons-group-label"
					name="row-radio-buttons-group"
				>
					<FormControlLabel value={"All"} control={<Radio checked={selectedTeam === 'All'} onChange={handleChange} />} label={"All"} />
					{
						teamData.map( (team: any) => <FormControlLabel value={team.team_name} control={<Radio checked={selectedTeam === team.team_name} onChange={handleChange} />} label={team.team_name} />	)
					}
				</RadioGroup>
			</FormControl>
			<MaterialTable
				title={`Case Efficiency Ledger`}
				columns={columns}
				data={tableData}
				isLoading={isFetching}
				icons={tableIcons}
				options={{
					pageSize: data?.length || 0,
					pageSizeOptions: [],
					paging: false,
					search: true,
					exportButton: true,
					columnsButton: true,
					sorting: true,
					maxBodyHeight: '650px',
					headerStyle: {
						backgroundColor: "#3f51b5",
						color: "#FFF",
						fontWeight: "bold",
						padding: "6px",
						position: 'sticky', 
						top: 0,
					},
					rowStyle: (rowData) => {
						const hasValidDates = !isInvalidDate(rowData["date_case_retained"]) && !isInvalidDate(rowData["projected_date_of_anticipated_attorney_fee"]);
						const projectedDate = hasValidDates ? new Date(rowData["projected_date_of_anticipated_attorney_fee"]) : null;
						const isPastDue = projectedDate && projectedDate < new Date();
						return {
							backgroundColor: isPastDue ? "#fffde7" : "inherit",
						};
					},
				}}
			/>
		</ThemeProvider>
	);
};

export default VariableQuotaLedgerTable;
